import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import SelectField from "../common/SelectField.js"
import { mlSheetService } from "../../services/mlSheetService"
import { shipmentService } from "../../services/shipmentService"
import companySettingsService from "../../services/companySettingsService.js"
import { paymentService } from "../../services/ShipmentPaymentService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from "../common/page_component/index.js"
import Loader from "../common/Loader"
import Alert from "../common/Alert"
import Button from "../common/Button.js"
import ListPagination from "../common/ListPagination.js"
import PaymentQrModal from "../common/PaymentQrModal.js"
import DeliveryOtpModal from "../common/DeliveryOtpModal.js"
import { Search, X, Package, RefreshCw, Undo2, Ban } from "lucide-react"
import { normalizeExact, shipmentMatchesSearch } from "../../utils/mlSheetShipmentSearch.js"
import { printShipmentReceipts } from "../../utils/printShipmentReceipt.js"
import { amountBeforeDemurrage, deliveryCollectAmount, demurrageDisplayAmount, getBulkStatusOptions, isCreditClientShipment, requiresDeliveryCollection } from "../../utils/shipmentPayment.js"
import { canRevertDelivery, canRevertReceiveToDispatch, revertDeliveryDisabledReason } from "../../utils/deliveryRevert.js"
import { isOtpRequiredResponse, normalizeOtpPending } from "../../utils/deliveryOtp.js"
import { useTranslation } from "react-i18next"
import { DEFAULT_LABOUR_CHARGE_POLICY, normalizeLabourChargePolicy, showLabourOnStatus } from "../../utils/labourChargePolicy.js"
import {
  shouldPickReceivingAgent,
  groupShipmentsByReceiverHub,
  buildReceiverAgentByShipment,
  validateHubAgentSelections,
} from "../../utils/receivingAgentPicker.js"
import usePaginatedListFetch from "../../utils/usePaginatedListFetch.js"
import { mergeLoadedPage } from "../../utils/mergeLoadedPage.js"
import CapturePhotoField from "../common/CapturePhotoField.js"
import { ArticleNames, QuantityValue, SenderBranchValue } from "./StatusShipmentFacts.js"
import HubAgentPickPanel from "./HubAgentPickPanel.js"

const isStatusUpdateLocked = (status) => String(status || "").toLowerCase() === "delivered"
const FILTER_OPTIONS = ["Dispatched", "Received", "Delivered"]
const STATUS_TAB_KEY = "dq.updateStatusTab"
const STATUS_HIDDEN_REFRESH_MS = 60000

const readStoredStatusTab = () => {
  try {
    const saved = sessionStorage.getItem(STATUS_TAB_KEY)
    if (FILTER_OPTIONS.includes(saved)) return saved
  } catch {
    /* keep the default tab when storage is unavailable */
  }
  return "Dispatched"
}

const rememberStatusTab = (status) => {
  try {
    sessionStorage.setItem(STATUS_TAB_KEY, status)
  } catch {
    /* the in-memory tab still stays selected */
  }
}

const canonicalStatus = (status) => {
  const value = String(status || "").toLowerCase()
  return FILTER_OPTIONS.find((option) => option.toLowerCase() === value) || ""
}

/** Pick one shipment when resolving which status tab to open after search. */
const resolveSearchTabMatch = (list, query) => {
  const trimmed = String(query || "").trim()
  if (!trimmed || !Array.isArray(list) || list.length === 0) return null
  const matches = list.filter((row) => shipmentMatchesSearch(row, trimmed))
  if (matches.length === 1) return matches[0]
  const byId = matches.filter(
    (row) => normalizeExact(row.shipment_id) === normalizeExact(trimmed),
  )
  if (byId.length >= 1) return byId[0]
  if (list.length === 1) return list[0]
  return null
}

const applyStatusTabFromMatch = (match, statusFilterRef, setStatusFilter) => {
  const tab = canonicalStatus(match?.status)
  if (!tab || tab === statusFilterRef.current) return
  statusFilterRef.current = tab
  rememberStatusTab(tab)
  setStatusFilter(tab)
}
const PAGE_SIZE = 10
const STATUS_SUBMIT_SELECTOR = '[data-agent-status-submit="true"]'

const focusVisibleSubmit = () => {
  const buttons = document.querySelectorAll(STATUS_SUBMIT_SELECTOR)
  const visible = Array.from(buttons).find((el) => el.offsetParent !== null)
  const target = visible || buttons[0]
  if (target && !target.disabled) target.focus()
}

const onEnter = (next) => (event) => {
  if (event.key !== "Enter") return
  event.preventDefault()
  if (typeof next === "function") next()
  else next?.current?.focus()
}

const isAgentOwnedHub = (shipment) =>
  String(shipment?.receiver_location_hub?.ownership_type || "").toUpperCase() === "AGENT"

const AgentUpdateStatus = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()

  const [shipments, setShipments] = useState([])
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [selectedMap, setSelectedMap] = useState(() => new Map())
  const [searchInput, setSearchInput] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState(readStoredStatusTab)
  const fetchSeqRef = useRef(0)
  const statusRequestRef = useRef(null)
  const statusFilterRef = useRef(statusFilter)
  const updatingRef = useRef(false)
  const alignedSelectRef = useRef("")
  const searchAlignSeqRef = useRef(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [selectableTotal, setSelectableTotal] = useState(0)
  const [allowDelivery, setAllowDelivery] = useState(
    user?.location_hub_allow_delivery !== false,
  )

  const [selectedStatus, setSelectedStatus] = useState(() => {
    const tab = readStoredStatusTab()
    return tab === "Received" ? "Delivered" : "Received"
  })
  const [remarks, setRemarks] = useState("Status updated via Update Status")

  const [deliveryReceiver, setDeliveryReceiver] = useState("")
  const [deliveryReceiverContact, setDeliveryReceiverContact] = useState("")
  const [deliveryReceiverImage, setDeliveryReceiverImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [deliveryGovIdImage, setDeliveryGovIdImage] = useState(null)
  const [govIdPreview, setGovIdPreview] = useState(null)
  const [unloadingCharges, setUnloadingCharges] = useState("")
  const [loadingCharges, setLoadingCharges] = useState("")
  const [labourPolicy, setLabourPolicy] = useState(DEFAULT_LABOUR_CHARGE_POLICY)
  const [chargeDefaults, setChargeDefaults] = useState({})

  const [paymentMode, setPaymentMode] = useState("upi")
  const [paymentAmount, setPaymentAmount] = useState("")
  const [demurrageAmount, setDemurrageAmount] = useState("")
  const [deliveryVehicleNo, setDeliveryVehicleNo] = useState("")
  const [lastMileHours, setLastMileHours] = useState("2")
  const [hubAgentPick, setHubAgentPick] = useState({})
  const [showPaymentQr, setShowPaymentQr] = useState(false)
  const [paymentIntent, setPaymentIntent] = useState(null)
  const [pendingDeliverAfterPay, setPendingDeliverAfterPay] = useState(null)
  const [otpPending, setOtpPending] = useState([])
  const [revertingId, setRevertingId] = useState("")
  const deliveryNameRef = useRef(null)
  const deliveryContactRef = useRef(null)
  const paymentAmountRef = useRef(null)
  const paymentModeRef = useRef(null)
  const deliveryFocusKeyRef = useRef("")

  const [isMobileLayout, setIsMobileLayout] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
  )

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)")
    const handler = (e) => setIsMobileLayout(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  const fetchData = useCallback(async (options = {}) => {
    const silent = options.silent === true
    if (silent && statusRequestRef.current) return
    if (statusRequestRef.current) statusRequestRef.current.abort()
    const controller = new AbortController()
    statusRequestRef.current = controller
    const requestId = ++fetchSeqRef.current
    const tabAtStart = statusFilterRef.current
    try {
      if (!silent) setLoading(true)
      const requestedSelect = currentPage === 1 ? String(searchParams.get("select") || "").trim() : ""
      const selectId = requestedSelect && alignedSelectRef.current !== requestedSelect ? requestedSelect : ""

      const response = await mlSheetService.getAgentStatusShipments({
        page: currentPage,
        perPage: PAGE_SIZE,
        search: searchQuery,
        statuses: [tabAtStart],
        includeShipmentId: selectId || undefined,
        signal: controller.signal,
      })
      if (requestId !== fetchSeqRef.current) return
      if (statusFilterRef.current !== tabAtStart) return

      const list = response.data?.shipments || []
      const pagination = response.data?.pagination || {}
      const total = pagination.total ?? response.data?.total ?? list.length
      const pages = pagination.pages || 1
      setShipments((prev) => mergeLoadedPage(currentPage, Array.isArray(list) ? list : [], prev, PAGE_SIZE))
      setTotalPages(pages)
      setTotalRecords(total)
      setSelectableTotal(
        pagination.selectable_total ??
          list.filter((s) => !isStatusUpdateLocked(s.status) && !s.is_deleted).length,
      )
      if (currentPage > pages) {
        setCurrentPage(pages)
      }
      if (requestId !== fetchSeqRef.current) return
      if (typeof response.data?.location_hub_allow_delivery === "boolean") {
        setAllowDelivery(response.data.location_hub_allow_delivery)
      } else if (user?.location_hub_allow_delivery !== undefined) {
        setAllowDelivery(user.location_hub_allow_delivery !== false)
      }
      setSelectedMap((prev) => {
        if (prev.size === 0) return prev
        const next = new Map(prev)
        let changed = false
        list.forEach((shipment) => {
          if (!next.has(shipment.shipment_id)) return
          if (isStatusUpdateLocked(shipment.status) || shipment.is_deleted) {
            next.delete(shipment.shipment_id)
            changed = true
            return
          }
          next.set(shipment.shipment_id, shipment)
          changed = true
        })
        return changed ? next : prev
      })
    } catch (error) {
      const canceled = controller.signal.aborted || error?.code === "ERR_CANCELED"
      if (canceled || silent) return
      console.error("Error fetching assigned shipments:", error)
      Alert.error(error.response?.data?.error || "Failed to load assigned shipments")
    } finally {
      if (statusRequestRef.current === controller) statusRequestRef.current = null
      if (requestId === fetchSeqRef.current) setLoading(false)
    }
  }, [
    user?.location_hub_allow_delivery,
    currentPage,
    searchQuery,
    statusFilter,
    searchParams,
  ])

  usePaginatedListFetch({
    fetchPage: fetchData,
    setCurrentPage,
    currentPage,
    resetPageDeps: [searchQuery, statusFilter],
  })

  useEffect(() => {
    setShipments([])
    setTotalPages(1)
    setTotalRecords(0)
  }, [searchQuery, statusFilter])

  useEffect(() => {
    statusFilterRef.current = statusFilter
    rememberStatusTab(statusFilter)
  }, [statusFilter])

  const lookupShipmentStatusTab = useCallback(async (query) => {
    const trimmed = String(query || "").trim()
    if (!trimmed) return null
    const response = await mlSheetService.getAgentStatusShipments({
      page: 1,
      perPage: PAGE_SIZE,
      search: trimmed,
      statuses: FILTER_OPTIONS,
      includeShipmentId: trimmed,
    })
    const list = response.data?.shipments || []
    return resolveSearchTabMatch(list, trimmed)
  }, [])

  useEffect(() => {
    const selectId = String(searchParams.get("select") || "").trim()
    if (!selectId || alignedSelectRef.current === selectId) return undefined
    let cancelled = false
    lookupShipmentStatusTab(selectId)
      .then((match) => {
        if (cancelled) return
        alignedSelectRef.current = selectId
        applyStatusTabFromMatch(match, statusFilterRef, setStatusFilter)
        setSearchInput(selectId)
        setSearchQuery((prev) => (prev === selectId ? prev : selectId))
      })
      .catch(() => {
        alignedSelectRef.current = selectId
      })
    return () => { cancelled = true }
  }, [searchParams, lookupShipmentStatusTab])

  useEffect(() => {
    let hiddenAt = 0
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now()
        return
      }
      if (updatingRef.current) return
      if (hiddenAt && Date.now() - hiddenAt < STATUS_HIDDEN_REFRESH_MS) return
      fetchData({ silent: true })
    }
    document.addEventListener("visibilitychange", onVisibility)
    return () => document.removeEventListener("visibilitychange", onVisibility)
  }, [fetchData])

  const applySearch = () => {
    const next = searchInput.trim()
    setSearchInput(next)
    if (!next) {
      setSearchQuery((prev) => (prev === "" ? prev : ""))
      return
    }
    const requestId = ++searchAlignSeqRef.current
    void lookupShipmentStatusTab(next)
      .then((match) => {
        if (requestId !== searchAlignSeqRef.current) return
        applyStatusTabFromMatch(match, statusFilterRef, setStatusFilter)
      })
      .catch(() => {})
      .finally(() => {
        if (requestId !== searchAlignSeqRef.current) return
        setSearchQuery((prev) => (prev === next ? prev : next))
      })
  }

  const clearSearch = () => {
    setSearchInput("")
    setSearchQuery((prev) => (prev === "" ? prev : ""))
  }

  const selectedShipments = useMemo(
    () => new Set(selectedMap.keys()),
    [selectedMap],
  )

  const filteredShipments = shipments

  const selectableFilteredShipments = useMemo(
    () => filteredShipments.filter((s) => !isStatusUpdateLocked(s.status) && !s.is_deleted),
    [filteredShipments],
  )

  const selectedList = useMemo(
    () =>
      Array.from(selectedMap.values()).filter(
        (s) => s && !isStatusUpdateLocked(s.status) && !s.is_deleted,
      ),
    [selectedMap],
  )

  const unpaidSelected = useMemo(
    () => selectedList.filter((s) => requiresDeliveryCollection(s)),
    [selectedList],
  )

  const creditSelected = useMemo(
    () => selectedList.filter((s) => isCreditClientShipment(s)),
    [selectedList],
  )

  const agentHubSelected = useMemo(
    () => selectedList.filter((s) => isAgentOwnedHub(s)),
    [selectedList],
  )
  const showUnloadingInput = showLabourOnStatus(
    labourPolicy,
    'unloading',
    selectedStatus,
    {
      agentHub: agentHubSelected.length > 0,
      chargeEnabled: !!chargeDefaults.unloading?.enabled,
    },
  )
  const showLoadingInput = showLabourOnStatus(
    labourPolicy,
    'loading',
    selectedStatus,
    {
      agentHub: agentHubSelected.length > 0,
      chargeEnabled: !!chargeDefaults.loading?.enabled,
    },
  )

  useEffect(() => {
    companySettingsService.getSettings()
      .then((res) => {
        setLabourPolicy(normalizeLabourChargePolicy(res?.data?.labour_charge_policy))
        setChargeDefaults(res?.data?.shipment_charge_defaults || {})
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!showUnloadingInput) {
      setUnloadingCharges("")
      return
    }
    const first = agentHubSelected[0] || selectedList[0]
    const existing = Number(first?.unloading_charges || 0)
    setUnloadingCharges(existing > 0 ? String(existing) : "")
  }, [showUnloadingInput, agentHubSelected, selectedList])

  useEffect(() => {
    if (!showLoadingInput) {
      setLoadingCharges("")
      return
    }
    const existing = Number(selectedList[0]?.loading_charges || 0)
    setLoadingCharges(existing > 0 ? String(existing) : "")
  }, [showLoadingInput, selectedList])

  const showReceivingAgentPicker = useMemo(
    () => shouldPickReceivingAgent(user, selectedStatus) && selectedList.length > 0,
    [user, selectedStatus, selectedList.length],
  )

  const receiverHubGroups = useMemo(
    () => groupShipmentsByReceiverHub(selectedList),
    [selectedList],
  )

  useEffect(() => {
    if (!showReceivingAgentPicker || receiverHubGroups.length === 0) {
      setHubAgentPick({})
      return
    }
    let cancelled = false
    setHubAgentPick((prev) => {
      const next = { ...prev }
      for (const group of receiverHubGroups) {
        next[group.hubId] = {
          ...(next[group.hubId] || {}),
          label: group.label,
          count: group.shipments.length,
          loading: true,
          agents: next[group.hubId]?.agents || [],
          selectedUserId: next[group.hubId]?.selectedUserId || "",
        }
      }
      return next
    })
    receiverHubGroups.forEach((group) => {
      shipmentService
        .getDestinationReceivingAgents(group.hubId)
        .then((res) => {
          if (cancelled) return
          const agents = res.data?.agents || []
          setHubAgentPick((prev) => {
            const kept = prev[group.hubId]?.selectedUserId
            const stillValid = agents.some((a) => a.user_id === kept)
            return {
              ...prev,
              [group.hubId]: {
                label: group.label,
                count: group.shipments.length,
                loading: false,
                agents,
                selectedUserId:
                  agents.length === 1
                    ? agents[0].user_id
                    : stillValid
                      ? kept
                      : "",
              },
            }
          })
        })
        .catch(() => {
          if (cancelled) return
          setHubAgentPick((prev) => ({
            ...prev,
            [group.hubId]: {
              label: group.label,
              count: group.shipments.length,
              loading: false,
              agents: [],
              selectedUserId: "",
            },
          }))
        })
    })
    return () => {
      cancelled = true
    }
  }, [showReceivingAgentPicker, receiverHubGroups])

  const adminEditsDemurrage = user?.role === "Admin"
    && selectedStatus === "Delivered"
    && selectedList.length === 1

  const dueForDelivery = (shipment) => {
    if (!shipment || isCreditClientShipment(shipment)) return 0
    if (adminEditsDemurrage && shipment.shipment_id === selectedList[0]?.shipment_id) {
      const demurrage = Math.max(0, Number(demurrageAmount) || 0)
      return Math.round((amountBeforeDemurrage(shipment) + demurrage) * 100) / 100
    }
    return deliveryCollectAmount(shipment)
  }

  const payableSelected = selectedList.filter((shipment) => dueForDelivery(shipment) > 0.009)

  const needsDeliveryPayment =
    selectedStatus === "Delivered" && payableSelected.length > 0

  const bulkStatusConfig = useMemo(
    () => getBulkStatusOptions(new Set(selectedList.map((s) => s.shipment_id)), selectedList),
    [selectedList],
  )

  const deliveryBlocked = user?.role === "Agent" && !allowDelivery

  const statusOptions = useMemo(() => {
    const base =
      bulkStatusConfig.options.length > 0
        ? bulkStatusConfig.options
        : deliveryBlocked
          ? ["Received"]
          : statusFilter === "Received"
            ? ["Delivered"]
            : statusFilter === "Dispatched"
              ? ["Received"]
              : ["Received", "Delivered"]
    let options = base.map((status) => ({ value: status, label: status }))
    if (deliveryBlocked) {
      options = options.filter((o) => o.value !== "Delivered")
    }
    return options
  }, [bulkStatusConfig.options, deliveryBlocked, statusFilter])

  useEffect(() => {
    const urlStatus = String(searchParams.get("status") || "").toLowerCase()
    if (urlStatus === "delivered") return
    const auto = bulkStatusConfig.autoSelect
    if (auto && (auto !== "Delivered" || !deliveryBlocked)) {
      setSelectedStatus(auto)
      return
    }
    setSelectedStatus((prev) => {
      const values = statusOptions.map((o) => o.value)
      if (values.includes(prev)) return prev
      return values[0] || "Received"
    })
  }, [bulkStatusConfig, statusOptions, deliveryBlocked, searchParams])

  useEffect(() => {
    const selectId = searchParams.get("select")
    if (!selectId || !shipments.length) return
    const match = shipments.find((s) => String(s.shipment_id) === String(selectId))
    if (match && !isStatusUpdateLocked(match.status) && !match.is_deleted) {
      setSelectedMap((prev) => {
        if (prev.has(match.shipment_id)) return prev
        const next = new Map(prev)
        next.set(match.shipment_id, match)
        return next
      })
    }
    if (String(searchParams.get("status") || "").toLowerCase() === "delivered" && !deliveryBlocked) {
      setSelectedStatus("Delivered")
    }
  }, [searchParams, shipments, deliveryBlocked])

  useEffect(() => {
    window.__jabbarStatusSelectedCount = selectedShipments.size
    const onSet = () => {
      if (selectedShipments.size > 0 && !deliveryBlocked) setSelectedStatus("Delivered")
    }
    window.addEventListener("jabbar-set-delivered", onSet)
    return () => {
      window.removeEventListener("jabbar-set-delivered", onSet)
      window.__jabbarStatusSelectedCount = 0
    }
  }, [selectedShipments, deliveryBlocked])

  useEffect(() => {
    setSelectedMap((prev) => {
      if (prev.size === 0) return prev
      let changed = false
      const next = new Map()
      prev.forEach((shipment, id) => {
        const statusOk = canonicalStatus(shipment.status) === statusFilter
        const searchOk = shipmentMatchesSearch(shipment, searchQuery)
        if (statusOk && searchOk && !isStatusUpdateLocked(shipment.status) && !shipment.is_deleted) {
          next.set(id, shipment)
        } else {
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [statusFilter, searchQuery])

  const demurrageSeed = adminEditsDemurrage
    ? `${selectedList[0]?.shipment_id || ""}:${Number(demurrageDisplayAmount(selectedList[0])).toFixed(2)}`
    : ""
  const payableDue = payableSelected.length === 1 ? dueForDelivery(payableSelected[0]) : null

  useEffect(() => {
    if (!demurrageSeed) return undefined
    setDemurrageAmount(demurrageSeed.slice(demurrageSeed.lastIndexOf(":") + 1))
    return undefined
  }, [demurrageSeed])

  useEffect(() => {
    if (needsDeliveryPayment && payableDue != null) {
      setPaymentAmount(Number(payableDue).toFixed(2))
    } else if (!needsDeliveryPayment) {
      setPaymentAmount("")
    }
  }, [needsDeliveryPayment, payableDue])

  useEffect(() => {
    if (loading || selectedStatus !== "Delivered" || deliveryBlocked || selectedList.length === 0) {
      return undefined
    }
    const firstId = selectedList[0]?.shipment_id || ""
    const key = `${selectedStatus}:${firstId}:${selectedList.length}`
    if (deliveryFocusKeyRef.current === key) return undefined
    deliveryFocusKeyRef.current = key
    const timer = window.setTimeout(() => {
      deliveryNameRef.current?.focus()
      deliveryNameRef.current?.select?.()
    }, 80)
    return () => window.clearTimeout(timer)
  }, [loading, selectedStatus, deliveryBlocked, selectedList])

  const goToAmountOrSubmit = () => {
    if (needsDeliveryPayment && payableSelected.length === 1 && paymentModeRef.current) {
      paymentModeRef.current.focus()
      return
    }
    focusVisibleSubmit()
  }

  const allFilteredSelected =
    selectableFilteredShipments.length > 0 &&
    selectableFilteredShipments.every((s) => selectedShipments.has(s.shipment_id))

  const selectStatusFilter = (status) => {
    if (!FILTER_OPTIONS.includes(status) || status === statusFilterRef.current) return
    statusFilterRef.current = status
    rememberStatusTab(status)
    setStatusFilter(status)
    if (status === "Received" && !deliveryBlocked) {
      setSelectedStatus("Delivered")
    } else if (status === "Dispatched") {
      setSelectedStatus("Received")
    }
  }

  const handleSelectAll = (e) => {
    const checked = e.target.checked
    setSelectedMap((prev) => {
      const next = new Map(prev)
      if (checked) {
        selectableFilteredShipments.forEach((s) => next.set(s.shipment_id, s))
      } else {
        selectableFilteredShipments.forEach((s) => next.delete(s.shipment_id))
      }
      return next
    })
  }

  const handleSelectShipment = (shipment) => {
    if (isStatusUpdateLocked(shipment.status)) return
    setSelectedMap((prev) => {
      const next = new Map(prev)
      if (next.has(shipment.shipment_id)) next.delete(shipment.shipment_id)
      else next.set(shipment.shipment_id, shipment)
      return next
    })
  }

  const handleRevertDelivery = async (shipment) => {
    const blocked = revertDeliveryDisabledReason(shipment, user)
    if (blocked) {
      Alert.error(blocked)
      return
    }
    const confirmed = await Alert.confirm(
      `Revert delivery for ${shipment.shipment_id}? It will move back to Received, payment will become To-Pay, and collected amount will be reversed.`,
      "Revert Delivery",
    )
    if (!confirmed) return
    try {
      setRevertingId(shipment.shipment_id)
      await shipmentService.revertDelivery(shipment.shipment_id)
      Alert.success("Delivery reverted to Received")
      await fetchData({ silent: true })
    } catch (err) {
      Alert.error(err.response?.data?.error || "Failed to revert delivery")
    } finally {
      setRevertingId("")
    }
  }

  const handleRevertReceive = async (shipment) => {
    if (!canRevertReceiveToDispatch(shipment, user)) return
    const confirmed = await Alert.confirm(
      `Send ${shipment.shipment_id} back to Dispatch? Unloading expense and cashbook for this receive will be reversed. Loading and booking commission stay.`,
      "Back to Dispatch",
    )
    if (!confirmed) return
    try {
      setRevertingId(shipment.shipment_id)
      await shipmentService.revertReceive(shipment.shipment_id)
      Alert.success("Parcel sent back to Dispatch")
      await fetchData({ silent: true })
    } catch (err) {
      Alert.error(err.response?.data?.error || "Failed to send parcel back to Dispatch")
    } finally {
      setRevertingId("")
    }
  }

  const previewOf = (file, setter) => {
    if (!file) {
      setter(null)
      return
    }
    const reader = new FileReader()
    reader.onloadend = () => setter(reader.result)
    reader.readAsDataURL(file)
  }

  const resetForm = () => {
    setSelectedMap(new Map())
    setDeliveryReceiver("")
    setDeliveryReceiverContact("")
    setDeliveryReceiverImage(null)
    setImagePreview(null)
    setDeliveryGovIdImage(null)
    setGovIdPreview(null)
    setPaymentAmount("")
    setPendingDeliverAfterPay(null)
    setUnloadingCharges("")
    setHubAgentPick({})
    setDeliveryVehicleNo("")
    setLastMileHours("2")
  }

  const runStatusUpdate = async (shipmentIds) => {
    const receiverAgentByShipment = showReceivingAgentPicker
      ? buildReceiverAgentByShipment(selectedList, hubAgentPick)
      : null
    const response = await shipmentService.bulkUpdateStatus(
      shipmentIds,
      selectedStatus,
      remarks,
      deliveryReceiver,
      deliveryReceiverImage,
      deliveryReceiverContact,
      showUnloadingInput ? unloadingCharges : null,
      showLoadingInput ? loadingCharges : null,
      deliveryGovIdImage,
      deliveryVehicleNo,
      lastMileHours,
      user?.role === "Admin" && selectedStatus === "Delivered" && shipmentIds.length === 1
        ? String(Math.max(0, Number(demurrageAmount) || 0))
        : null,
      null,
      receiverAgentByShipment,
    )
    if (isOtpRequiredResponse(response.data)) {
      setOtpPending(normalizeOtpPending(response.data))
      if (response.data.updated_count > 0) {
        Alert.success(
          `Updated ${response.data.updated_count} shipment(s). Verify OTP to deliver the rest.`,
        )
      } else {
        Alert.success(response.data.message || "OTP sent on WhatsApp")
      }
      await fetchData({ silent: true })
      return
    }
    if (response.data?.success) {
      const failed = response.data.failed_shipments || []
      if (failed.length > 0 && response.data.updated_count === 0) {
        Alert.error(failed[0]?.reason || "Failed to update shipments")
      } else {
        const ewayNote = (response.data.eway_bill_warnings || []).filter(Boolean).join(" ")
        Alert.success(
          `Successfully updated ${response.data.updated_count} shipment(s) to ${selectedStatus}${ewayNote ? `. ${ewayNote}` : ""}`,
        )
        if (selectedStatus === "Delivered") {
          await printShipmentReceipts(shipmentIds, user)
        }
      }
      resetForm()
      await fetchData({ silent: true })
    } else {
      Alert.error(response.data?.error || "Failed to update shipments")
    }
  }

  const collectDeliveryPayment = async (shipment) => {
    const due = dueForDelivery(shipment)
    const amount = parseFloat(paymentAmount || 0)
    if (Number.isNaN(amount) || amount <= 0) {
      throw new Error("Enter a valid payment amount")
    }
    if (amount > due + 0.001) {
      throw new Error(`Payment cannot exceed the amount to collect (₹${due.toFixed(2)})`)
    }
    if (amount + 0.001 < due) {
      throw new Error(`Pay the full amount (₹${due.toFixed(2)}) to mark as Delivered`)
    }

    const payload = new FormData()
    payload.append("payment_amount", String(amount))
    payload.append("payment_mode", paymentMode)
    payload.append("apply_demurrage", "1")
    if (user?.role === "Admin") {
      payload.append("demurrage_amount", String(Math.max(0, Number(demurrageAmount) || 0)))
    }
    payload.append("remarks", `${shipment.shipment_id} has been delivered`)

    const response = await paymentService.addPayment(shipment.shipment_id, payload)
    if (response.data?.requires_online_payment && response.data?.payment_intent) {
      setPaymentIntent(response.data.payment_intent)
      setPendingDeliverAfterPay([shipment.shipment_id])
      setShowPaymentQr(true)
      return { deferred: true }
    }
    return { deferred: false }
  }

  const handleBulkStatusUpdate = async () => {
    const shipmentIds = selectedList.map((s) => s.shipment_id)
    if (shipmentIds.length === 0) {
      Alert.error("Please select at least one shipment that is not already Delivered")
      return
    }
    if (!selectedStatus) {
      Alert.error("Please select a status")
      return
    }
    if (selectedStatus === "Delivered" && deliveryBlocked) {
      Alert.error("Delivery is not allowed for your assigned hub")
      return
    }
    const proofsRequired = user?.delivery_details_required !== false
    if (selectedStatus === "Delivered" && !deliveryBlocked && proofsRequired) {
      if (!String(deliveryReceiver || "").trim()) {
        Alert.error("Receiver name is required")
        return
      }
      if (!String(deliveryReceiverContact || "").trim()) {
        Alert.error("Receiver contact is required")
        return
      }
      if (!deliveryReceiverImage) {
        Alert.error("Delivery photo is required")
        return
      }
      if (!deliveryGovIdImage) {
        Alert.error("Government ID proof is required")
        return
      }
    }
    if (!statusOptions.some((o) => o.value === selectedStatus)) {
      Alert.error("Selected status is not allowed for the chosen shipments")
      return
    }
    if (selectedStatus === "Delivered" && payableSelected.length > 1) {
      Alert.error(
        "Multiple unpaid shipments selected. Select one at a time to collect delivery payment.",
      )
      return
    }
    if (showReceivingAgentPicker) {
      const pickErr = validateHubAgentSelections(selectedList, hubAgentPick)
      if (pickErr) {
        Alert.error(pickErr)
        return
      }
    }

    try {
      setUpdating(true)
      updatingRef.current = true
      if (needsDeliveryPayment && payableSelected.length === 1) {
        const result = await collectDeliveryPayment(payableSelected[0])
        if (result.deferred) return
      }
      await runStatusUpdate(shipmentIds)
    } catch (error) {
      const message = error.response?.data?.error || error.message || "Failed to update shipments"
      if (message !== "Payment cancelled") Alert.error(message)
    } finally {
      updatingRef.current = false
      setUpdating(false)
    }
  }

  const handleOnlinePaymentSuccess = async () => {
    setShowPaymentQr(false)
    setPaymentIntent(null)
    const ids = pendingDeliverAfterPay
    setPendingDeliverAfterPay(null)
    try {
      setUpdating(true)
      if (ids?.length) {
        await runStatusUpdate(ids)
      } else {
        Alert.success("Payment received")
        await fetchData({ silent: true })
      }
    } catch (error) {
      Alert.error(error.response?.data?.error || "Payment received but status update failed")
      await fetchData({ silent: true })
    } finally {
      setUpdating(false)
    }
  }

  const handleOnlinePaymentCancel = () => {
    setShowPaymentQr(false)
    setPaymentIntent(null)
    setPendingDeliverAfterPay(null)
    setUpdating(false)
  }

  const getShipmentStatusColor = (status) => {
    const statusMap = {
      booked: "bg-blue-100 text-blue-800",
      dispatched: "bg-purple-100 text-purple-800",
      received: "bg-yellow-100 text-yellow-800",
      delivered: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      returned: "bg-orange-100 text-orange-800",
    }
    return statusMap[status?.toLowerCase()] || "bg-gray-100 text-gray-800"
  }

  if (loading && shipments.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading Update Status..." />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className={isMobileLayout ? "pb-24 min-h-0" : ""}>
        {isMobileLayout ? (
          <div className="sticky top-0 z-30 bg-white shrink-0 mb-2 pb-2 border-b border-gray-100">
            <h1 className="flex items-center gap-1.5 text-sm font-bold text-secondary truncate min-w-0">
              <Package className="w-4 h-4 text-buttonhoverTxt shrink-0" />
              <span className="truncate">{t("sidebar.updateStatus", "Update Status")}</span>
            </h1>
            <p className="text-[10px] text-gray-500 mt-1">
              ML sheet shipments assigned to you
            </p>
          </div>
        ) : (
          <div className="p-4 sm:p-6 sm:px-0 mb-4 sm:py-2 sm:mb-2">
            <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2">
              <Package className="w-6 h-6 text-buttonhoverTxt" />
              {t("sidebar.updateStatus", "Update Status")}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {user?.role === "Agent"
                ? "Incoming shipments for your branch only. Parcels you booked and dispatched stay off this page."
                : "Shipments linked to Master Loading Sheets you can manage."}
            </p>
          </div>
        )}

        {deliveryBlocked && (
          <div className="mb-3 sm:mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs sm:text-sm">
            Delivery is not enabled for your location hub. You can mark shipments as Received,
            but Delivered is disabled.
          </div>
        )}

        <div className="bg-white rounded-xl p-3 sm:p-4 mb-3 sm:mb-6 shadow-sm md:shadow-none">
          <h3 className="hidden md:block text-base sm:text-lg font-semibold text-gray-700 mb-4 sm:mb-6">
            Update Status
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-4">
            <div className="min-w-0">
              <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                Select Status <span className="text-red-500">*</span>
              </label>
              <SelectField
                variant="form"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                options={statusOptions}
                className="min-w-0 w-full"
              />
              <p className="text-[9px] sm:text-xs text-gray-500 mt-1 leading-snug">
                Dispatched → Received. Already Received → Delivered
                {deliveryBlocked ? " (hub delivery off)" : " (collect payment if To-Pay)"}.
              </p>
              {bulkStatusConfig.paymentMessage && (
                <p className="text-[9px] sm:text-xs text-amber-700 mt-1">{bulkStatusConfig.paymentMessage}</p>
              )}
            </div>

            <div className="min-w-0">
              <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                Selected
              </label>
              <div className="flex items-center justify-center px-2 sm:px-4 py-2 sm:py-3 border rounded-xl bg-blue-50/50 min-h-[38px] sm:min-h-[42px]">
                <div className="text-xs sm:text-sm font-bold text-secondary">{selectedList.length}</div>
                <div className="text-[10px] sm:text-sm text-secondary font-medium ml-1 truncate">
                  / {selectableTotal || selectableFilteredShipments.length} upd
                </div>
              </div>
            </div>

            <div className="col-span-2 md:col-span-1 min-w-0">
              <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                Remarks
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Optional remarks..."
                className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 resize-none md:border-2"
                rows={isMobileLayout ? 2 : 1}
              />
            </div>
          </div>

          {showReceivingAgentPicker && (
            <HubAgentPickPanel
              groups={receiverHubGroups}
              hubAgentPick={hubAgentPick}
              onSelectAgent={(hubId, value) => {
                setHubAgentPick((prev) => ({
                  ...prev,
                  [hubId]: {
                    ...prev[hubId],
                    selectedUserId: value,
                  },
                }))
              }}
            />
          )}

          {(showUnloadingInput || showLoadingInput) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-4 p-2 sm:p-4 bg-blue-50 rounded-xl border border-blue-200">
              <div className="sm:col-span-3">
                <h4 className="text-[10px] sm:text-sm font-semibold text-blue-800">
                  Labour charges
                </h4>
              </div>
              {showLoadingInput && (
                <div className="min-w-0">
                  <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                    Loading charge
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={loadingCharges}
                    onChange={(e) => setLoadingCharges(e.target.value)}
                    placeholder="₹ loading"
                    className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                  />
                </div>
              )}
              {showUnloadingInput && (
              <div className="min-w-0">
                <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                  Unloading charge
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={unloadingCharges}
                  onChange={(e) => setUnloadingCharges(e.target.value)}
                  placeholder="₹ unloading"
                  className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                />
              </div>
              )}
            </div>
          )}

          {selectedStatus === "Delivered" && !deliveryBlocked && (
            <div className="mb-3 sm:mb-4 px-2 sm:px-3 py-2.5 rounded-xl border border-orange-200 bg-orange-50">
              <div className="flex flex-wrap items-end gap-x-3 sm:gap-x-4 gap-y-3">
                <div className="min-w-[7rem] flex-[1_1_9rem]">
                  <label className="block text-xs font-semibold text-gray-800 mb-1">
                    Receiver civil name
                    {user?.delivery_details_required !== false ? (
                      <span className="text-red-500"> *</span>
                    ) : null}
                  </label>
                  <input
                    ref={deliveryNameRef}
                    type="text"
                    value={deliveryReceiver}
                    onChange={(e) => setDeliveryReceiver(e.target.value)}
                    onKeyDown={onEnter(() => deliveryContactRef.current?.focus())}
                    placeholder="Full name"
                    className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-blue-400"
                    maxLength={250}
                  />
                </div>
                <div className="min-w-[6rem] flex-[1_1_8rem]">
                  <label className="block text-xs font-semibold text-gray-800 mb-1">
                    Contact number
                    {user?.delivery_details_required !== false ? (
                      <span className="text-red-500"> *</span>
                    ) : null}
                  </label>
                  <input
                    ref={deliveryContactRef}
                    type="text"
                    value={deliveryReceiverContact}
                    onChange={(e) =>
                      setDeliveryReceiverContact(e.target.value.replace(/[^\d]/g, "").slice(0, 10))
                    }
                    onKeyDown={onEnter(goToAmountOrSubmit)}
                    placeholder="Mobile"
                    className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-blue-400"
                    maxLength={10}
                  />
                </div>
                <div className="min-w-[7rem] flex-[1_1_9rem]">
                  <label className="block text-xs font-semibold text-gray-800 mb-1">
                    Last-mile vehicle
                  </label>
                  <input
                    type="text"
                    value={deliveryVehicleNo}
                    onChange={(e) => setDeliveryVehicleNo(e.target.value.toUpperCase())}
                    placeholder="Needed when an e-Way Bill is on the GC"
                    className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-blue-400"
                    maxLength={20}
                  />
                </div>
                <div className="min-w-[6rem] flex-[1_1_8rem]">
                  <label className="block text-xs font-semibold text-gray-800 mb-1">
                    E-Way close window
                  </label>
                  <select
                    value={lastMileHours}
                    onChange={(e) => setLastMileHours(e.target.value)}
                    className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:border-blue-400"
                  >
                    <option value="1">1 hour</option>
                    <option value="2">2 hours</option>
                    <option value="3">3 hours</option>
                  </select>
                </div>
                <CapturePhotoField
                  compact
                  label="Delivery photo"
                  required={user?.delivery_details_required !== false}
                  file={deliveryReceiverImage}
                  disabled={updating}
                  onPick={(file) => {
                    setDeliveryReceiverImage(file)
                    previewOf(file, setImagePreview)
                  }}
                  onClear={() => {
                    setDeliveryReceiverImage(null)
                    setImagePreview(null)
                  }}
                />
                <CapturePhotoField
                  compact
                  label="Government ID proof"
                  required={user?.delivery_details_required !== false}
                  file={deliveryGovIdImage}
                  disabled={updating}
                  onPick={(file) => {
                    setDeliveryGovIdImage(file)
                    previewOf(file, setGovIdPreview)
                  }}
                  onClear={() => {
                    setDeliveryGovIdImage(null)
                    setGovIdPreview(null)
                  }}
                />
              </div>
            </div>
          )}

          {adminEditsDemurrage && !deliveryBlocked && (
            <div className="mb-3 sm:mb-4 p-3 sm:p-4 rounded-xl border border-amber-200 bg-amber-50">
              <label className="block text-xs sm:text-sm font-semibold text-amber-900 mb-1">
                Demurrage
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={demurrageAmount}
                onChange={(event) => setDemurrageAmount(event.target.value)}
                onWheel={(event) => event.currentTarget.blur()}
                className="w-full sm:max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-xl bg-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <p className="text-xs text-gray-600 mt-1">
                Calculated amount is filled in. Change it before marking this shipment Delivered.
              </p>
            </div>
          )}

          {selectedStatus === "Delivered" && !deliveryBlocked && creditSelected.length > 0 && (
            <div className="mb-3 sm:mb-4 p-3 sm:p-4 rounded-xl border border-sky-200 bg-sky-50 text-sky-950">
              <h4 className="text-xs sm:text-sm font-semibold">This is a credit client</h4>
              <p className="text-xs sm:text-sm mt-1">
                {creditSelected
                  .map((s) => {
                    const who = s.shipment_client_company || s.shipment_client_name
                    return who ? `${s.shipment_id} (${who})` : s.shipment_id
                  })
                  .join(", ")}
                . Do not collect the amount or payment mode. The freight stays on the shipment client.
              </p>
            </div>
          )}

          {needsDeliveryPayment && !deliveryBlocked && (
            <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-amber-50 rounded-xl border border-amber-200">
              <h4 className="text-xs sm:text-sm font-semibold text-amber-900 mb-2">
                Collect payment on delivery
              </h4>
              {payableSelected.length > 1 ? (
                <p className="text-xs sm:text-sm text-red-600">
                  Select only one unpaid shipment to collect Cash or UPI, then mark Delivered.
                </p>
              ) : (
                <>
                  <p className="text-xs sm:text-sm text-gray-700 mb-3">
                    <span className="font-semibold">{payableSelected[0].shipment_id}</span>{" "}
                    <span className="font-semibold">{payableSelected[0].mode_of_payment || "Amount"}</span>
                    {amountBeforeDemurrage(payableSelected[0]) > 0.009 && (
                      <>
                        {" "}
                        <span className="font-semibold">
                          ₹{amountBeforeDemurrage(payableSelected[0]).toFixed(2)}
                        </span>
                      </>
                    )}
                    {(adminEditsDemurrage ? Math.max(0, Number(demurrageAmount) || 0) : demurrageDisplayAmount(payableSelected[0])) > 0.009 && (
                      <>
                        {" "}+ Demurrage{" "}
                        <span className="font-semibold">
                          ₹{(adminEditsDemurrage ? Math.max(0, Number(demurrageAmount) || 0) : demurrageDisplayAmount(payableSelected[0])).toFixed(2)}
                        </span>
                      </>
                    )}
                    . Collect ₹{dueForDelivery(payableSelected[0]).toFixed(2)} to mark Delivered.
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <div>
                      <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1">
                        Amount
                      </label>
                      <input
                        ref={paymentAmountRef}
                        type="number"
                        min="0"
                        step="0.01"
                        value={paymentAmount}
                        readOnly
                        tabIndex={-1}
                        aria-readonly="true"
                        title="Full balance is locked for delivery payment"
                        className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-xl bg-gray-100 text-gray-800 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1">
                        Method
                      </label>
                      <select
                        ref={paymentModeRef}
                        value={paymentMode}
                        onChange={(e) => setPaymentMode(e.target.value)}
                        onKeyDown={onEnter(focusVisibleSubmit)}
                        className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-xl"
                      >
                        <option value="upi">UPI (QR)</option>
                        <option value="cash">Cash</option>
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          <Button
            variant="primary"
            type="button"
            data-agent-status-submit="true"
            onClick={handleBulkStatusUpdate}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !updating) {
                event.preventDefault()
                handleBulkStatusUpdate()
              }
            }}
            disabled={
              updating ||
              selectedList.length === 0 ||
              (needsDeliveryPayment && payableSelected.length > 1)
            }
            loading={updating}
            className="hidden md:inline-flex"
          >
            {updating
              ? "Updating..."
              : needsDeliveryPayment
                ? `Pay & Update ${selectedList.length} Shipment(s)`
                : `Update ${selectedList.length} Shipment(s)`}
          </Button>
        </div>

        <div className={`bg-white rounded-xl shadow-md overflow-hidden max-w-full ${loading ? "opacity-70" : ""}`}>
          <div className="px-3 sm:px-6 py-2 sm:py-4 border-b border-gray-200 space-y-2 sm:space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={handleSelectAll}
                  disabled={selectableFilteredShipments.length === 0}
                  className="w-4 h-4 sm:w-5 sm:h-5 rounded border-2 border-gray-400 cursor-pointer accent-blue-500 disabled:opacity-40"
                />
                <h3 className="text-xs sm:text-lg font-semibold text-gray-700 mb-0">
                  Shipments (
                  {totalRecords === 0
                    ? 0
                    : `${Math.min(shipments.length, totalRecords)} of ${totalRecords}`}
                  )
                </h3>
              </div>
              <div className="flex flex-1 min-w-0 items-center gap-2">
                <div className="relative flex-1 min-w-0">
                  <button
                    type="button"
                    onClick={applySearch}
                    aria-label="Search"
                    className="absolute left-1.5 sm:left-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-blue-600"
                  >
                    <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        applySearch()
                      }
                    }}
                    placeholder="Exact match: ID, name, contact, ML sheet..."
                    className="w-full pl-8 sm:pl-10 pr-8 sm:pr-10 py-1.5 sm:py-2.5 border border-gray-300 rounded-xl text-[11px] sm:text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-orange-400"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                      aria-label="Clear search"
                    >
                      <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={applySearch}
                  className="shrink-0 px-3 sm:px-4 py-1.5 sm:py-2.5 rounded-xl bg-blue-600 text-white text-[11px] sm:text-sm font-semibold hover:bg-blue-700"
                >
                  Enter
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <span className="text-[10px] sm:text-xs font-semibold text-gray-600 mr-0.5">
                Status:
              </span>
              {FILTER_OPTIONS.map((status) => {
                const active = statusFilter === status
                return (
                  <button
                    key={status}
                    type="button"
                    aria-pressed={active}
                    onClick={() => selectStatusFilter(status)}
                    className={`px-2.5 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-semibold rounded-xl border transition-colors ${
                      active
                        ? "bg-secondary text-white border-secondary"
                        : "bg-white text-gray-600 border-gray-300 hover:border-secondary"
                    }`}
                  >
                    {status}
                  </button>
                )
              })}
            </div>
          </div>

          {filteredShipments.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="text-gray-500 text-sm sm:text-lg">
                {searchQuery.trim()
                  ? "No shipments match the search"
                  : "No shipments match the selected filters"}
              </p>
            </div>
          ) : (
            <>
              <div className="hidden xl:block w-full overflow-x-auto">
                <table className="w-full min-w-[1180px] border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left w-12">
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          onChange={handleSelectAll}
                          disabled={selectableFilteredShipments.length === 0}
                          className="w-4 h-4 rounded border border-gray-400 cursor-pointer accent-blue-500"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Shipment ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        ML Sheet
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Sender
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Sender Branch
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Receiver
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Article
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Amount / Balance
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredShipments.map((shipment, index) => {
                      const locked = isStatusUpdateLocked(shipment.status)
                      const canRevert = canRevertDelivery(shipment, user)
                      const revertBlockedReason = revertDeliveryDisabledReason(shipment, user)
                      return (
                        <tr
                          key={shipment.id || shipment.shipment_id}
                          className={`${index % 2 === 0 ? "bg-white" : "bg-gray-50"} ${
                            locked ? "bg-green-50/40" : "hover:bg-gray-50"
                          } ${
                            selectedShipments.has(shipment.shipment_id)
                              ? "ring-1 ring-inset ring-orange-300"
                              : ""
                          }`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedShipments.has(shipment.shipment_id)}
                              onChange={() => handleSelectShipment(shipment)}
                              disabled={locked}
                              title={locked ? "Already delivered — locked" : undefined}
                              className="w-4 h-4 rounded border border-gray-400 accent-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                              className="font-semibold text-secondary hover:text-orange-600 hover:underline"
                            >
                              {shipment.shipment_id}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-800">
                            {shipment.ml_sheet_id || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="font-semibold text-gray-900">{shipment.sender_name}</div>
                            <div className="text-xs text-gray-600">{shipment.sender_contact}</div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <SenderBranchValue shipment={shipment} />
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="font-semibold text-gray-900">{shipment.receiver_name}</div>
                            <div className="text-xs text-gray-600">{shipment.receiver_contact}</div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <ArticleNames shipment={shipment} />
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            <QuantityValue shipment={shipment} />
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            <div className="font-semibold text-gray-900">
                              ₹{parseFloat(shipment.total_amount || 0).toFixed(2)}
                            </div>
                            {isCreditClientShipment(shipment) ? (
                              <div className="text-xs text-sky-800 font-semibold">
                                On shipment client
                              </div>
                            ) : (
                            <div
                              className={
                                parseFloat(shipment.balance_amount || 0) > 0
                                  ? "text-xs text-amber-700 font-semibold"
                                  : "text-xs text-green-700"
                              }
                            >
                              {demurrageDisplayAmount(shipment) > 0.009
                                ? `${amountBeforeDemurrage(shipment) > 0.009 ? `${shipment.mode_of_payment || "Due"} ₹${amountBeforeDemurrage(shipment).toFixed(2)} · ` : `${shipment.mode_of_payment || "Paid"} · `}Demurrage ₹${demurrageDisplayAmount(shipment).toFixed(2)}`
                                : `Bal: ₹${parseFloat(shipment.balance_amount || 0).toFixed(2)}`}
                            </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase ${getShipmentStatusColor(
                                shipment.status,
                              )}`}
                            >
                              {shipment.status}
                            </span>
                            <div className="text-xs text-gray-600 mt-1">
                              {isCreditClientShipment(shipment) ? "Credit client" : shipment.mode_of_payment}
                            </div>
                            {locked && (
                              <div className="text-xs text-red-600 font-semibold mt-0.5">Locked</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-center">
                            <div className="flex items-center justify-center gap-2">
                              {locked && (
                                canRevert ? (
                                  <button
                                    type="button"
                                    onClick={() => handleRevertDelivery(shipment)}
                                    disabled={revertingId === shipment.shipment_id}
                                    title="Revert delivery to Received"
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-amber-700 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    <Undo2 className={`w-4 h-4 ${revertingId === shipment.shipment_id ? "animate-spin" : ""}`} />
                                  </button>
                                ) : (
                                  <span
                                    title={revertBlockedReason || "Revert is not available"}
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 bg-gray-100 cursor-not-allowed"
                                    aria-disabled="true"
                                  >
                                    <Ban className="w-4 h-4" />
                                  </span>
                                )
                              )}
                              {canRevertReceiveToDispatch(shipment, user) && (
                                <button
                                  type="button"
                                  onClick={() => handleRevertReceive(shipment)}
                                  disabled={revertingId === shipment.shipment_id}
                                  title="Send back to Dispatch"
                                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-sky-700 hover:bg-sky-50 disabled:opacity-40"
                                >
                                  <Undo2 className={`w-4 h-4 ${revertingId === shipment.shipment_id ? "animate-spin" : ""}`} />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                                className="text-orange-600 hover:text-orange-700 font-semibold text-sm hover:underline"
                              >
                                View →
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className="xl:hidden space-y-2 p-2 sm:p-4">
                {filteredShipments.map((shipment) => {
                  const isSelected = selectedShipments.has(shipment.shipment_id)
                  const locked = isStatusUpdateLocked(shipment.status)
                  const canRevert = canRevertDelivery(shipment, user)
                  const revertBlockedReason = revertDeliveryDisabledReason(shipment, user)
                  return (
                    <div
                      key={shipment.id || shipment.shipment_id}
                      className={`bg-white rounded-xl shadow-sm border p-2.5 sm:p-4 border-l-4 transition-colors ${
                        locked
                          ? "border-gray-200 border-l-green-500 bg-green-50/30"
                          : isSelected
                            ? "border-orange-400 border-l-orange-500 bg-orange-50/40"
                            : "border-gray-100 border-l-orange-500"
                      }`}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectShipment(shipment)}
                          disabled={locked}
                          className="w-4 h-4 rounded border-2 border-gray-400 accent-blue-500 mt-0.5 shrink-0 disabled:opacity-50"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-start justify-between gap-1">
                            <button
                              type="button"
                              onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                              className="text-orange-600 font-bold text-xs sm:text-sm break-all hover:underline text-left"
                            >
                              {shipment.shipment_id}
                            </button>
                            <span
                              className={`px-1.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase shrink-0 ${getShipmentStatusColor(
                                shipment.status,
                              )}`}
                            >
                              {shipment.status}
                            </span>
                          </div>
                          <p className="text-[10px] text-gray-600 mt-0.5">
                            ML: {shipment.ml_sheet_id || "—"}
                          </p>
                          <p className="text-xs sm:text-sm font-bold text-gray-900 mt-1">
                            ₹{parseFloat(shipment.total_amount || 0).toFixed(2)}
                            {isCreditClientShipment(shipment) ? (
                              <span className="ml-2 text-sky-800 font-semibold">Credit client</span>
                            ) : parseFloat(shipment.balance_amount || 0) > 0 ? (
                              <span className="ml-2 text-amber-700 font-semibold">
                                {demurrageDisplayAmount(shipment) > 0.009
                                  ? `${amountBeforeDemurrage(shipment) > 0.009 ? `${shipment.mode_of_payment || "Due"} ₹${amountBeforeDemurrage(shipment).toFixed(2)} · ` : `${shipment.mode_of_payment || "Paid"} · `}Demurrage ₹${demurrageDisplayAmount(shipment).toFixed(2)}`
                                  : `Bal ₹${parseFloat(shipment.balance_amount || 0).toFixed(2)}`}
                              </span>
                            ) : null}
                          </p>
                          {locked && (
                            <p className="text-[10px] text-red-600 font-semibold mt-1">
                              Already delivered — locked
                            </p>
                          )}
                          {locked && (
                            canRevert ? (
                              <button
                                type="button"
                                onClick={() => handleRevertDelivery(shipment)}
                                disabled={revertingId === shipment.shipment_id}
                                title="Revert delivery to Received"
                                className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 disabled:opacity-40"
                              >
                                <Undo2 className={`w-3.5 h-3.5 ${revertingId === shipment.shipment_id ? "animate-spin" : ""}`} />
                                Revert delivery
                              </button>
                            ) : (
                              <span
                                title={revertBlockedReason || "Revert is not available"}
                                className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-gray-400 cursor-not-allowed"
                                aria-disabled="true"
                              >
                                <Ban className="w-3.5 h-3.5" />
                                Revert disabled
                              </span>
                            )
                          )}
                          {canRevertReceiveToDispatch(shipment, user) && (
                            <button
                              type="button"
                              onClick={() => handleRevertReceive(shipment)}
                              disabled={revertingId === shipment.shipment_id}
                              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700 disabled:opacity-40"
                            >
                              <Undo2 className={`w-3.5 h-3.5 ${revertingId === shipment.shipment_id ? "animate-spin" : ""}`} />
                              Back to dispatch
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[10px] sm:text-sm">
                        <div className="min-w-0">
                          <p className="text-gray-600 font-medium">Sender</p>
                          <p className="font-semibold text-gray-900 break-words leading-tight">
                            {shipment.sender_name}
                          </p>
                          <p className="text-gray-600">{shipment.sender_contact}</p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-gray-600 font-medium">Receiver</p>
                          <p className="font-semibold text-gray-900 break-words leading-tight">
                            {shipment.receiver_name}
                          </p>
                          <p className="text-gray-600">{shipment.receiver_contact}</p>
                        </div>
                        <div className="min-w-0 col-span-2">
                          <p className="text-gray-600 font-medium">Sender Branch</p>
                          <p className="font-semibold text-gray-900 break-words leading-tight">
                            <SenderBranchValue shipment={shipment} />
                          </p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-gray-600 font-medium">Article</p>
                          <p className="font-semibold text-gray-900 break-words leading-tight">
                            <ArticleNames shipment={shipment} />
                          </p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-gray-600 font-medium">Quantity</p>
                          <p className="font-semibold text-gray-900">
                            <QuantityValue shipment={shipment} />
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              {totalRecords > 0 && (
                <ListPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      loading={loading && currentPage > 1}
                      onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
                    />
              )}
            </>
          )}
        </div>
      </div>

      {isMobileLayout && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] md:hidden">
          <Button
            variant="primary"
            type="button"
            icon={RefreshCw}
            data-agent-status-submit="true"
            onClick={handleBulkStatusUpdate}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !updating) {
                event.preventDefault()
                handleBulkStatusUpdate()
              }
            }}
            disabled={
              updating ||
              selectedList.length === 0 ||
              (needsDeliveryPayment && payableSelected.length > 1)
            }
            loading={updating}
            fullWidth
            className="w-full justify-center"
          >
            {updating
              ? "Updating..."
              : needsDeliveryPayment
                ? `Pay & Update ${selectedList.length}`
                : `Update ${selectedList.length} Shipment(s)`}
          </Button>
        </div>
      )}

      <PaymentQrModal
        open={showPaymentQr}
        intent={paymentIntent}
        title="Scan UPI QR to Pay"
        onSuccess={handleOnlinePaymentSuccess}
        onCancel={handleOnlinePaymentCancel}
      />
      <DeliveryOtpModal
        open={otpPending.length > 0}
        pending={otpPending}
        onClose={() => setOtpPending([])}
        onVerified={async () => {
          const deliveredIds = otpPending.map((row) => row.shipment_id).filter(Boolean)
          setOtpPending([])
          resetForm()
          Alert.success("Delivery verified and completed")
          await printShipmentReceipts(deliveredIds, user)
          await fetchData({ silent: true })
        }}
      />
    </DashboardLayout>
  )
}

export default AgentUpdateStatus
